/**
 * Loading Skeleton Components for Suspense Fallbacks
 * Provides pre-built skeleton layouts for different page types
 */

import { Skeleton } from './Skeleton';

/**
 * Generic page skeleton for standard pages
 */
export function PageSkeleton() {
  return (
    <div className="flex items-center justify-center min-h-[60vh]">
      <div className="w-full max-w-2xl px-4 space-y-4">
        <Skeleton variant="text" height="2rem" width="60%" className="mx-auto" />
        <Skeleton variant="card" height="12rem" />
        <Skeleton variant="card" height="8rem" />
        <Skeleton variant="text" lines={3} />
      </div>
    </div>
  );
}

/**
 * Skeleton for landing page with hero section
 */
export function LandingSkeleton() {
  return (
    <div className="min-h-[calc(100vh-4rem)] flex flex-col items-center justify-center px-4 py-16">
      <div className="max-w-3xl mx-auto text-center w-full">
        {/* Hero headline skeleton */}
        <Skeleton variant="text" height="3rem" width="80%" className="mx-auto mb-4" />
        <Skeleton variant="text" height="1.5rem" width="50%" className="mx-auto mb-3" />
        <Skeleton variant="text" height="1rem" width="40%" className="mx-auto mb-10" />
        
        {/* Search bar skeleton */}
        <div className="flex flex-col sm:flex-row gap-3 max-w-2xl mx-auto mb-6">
          <Skeleton variant="text" height="3.5rem" className="flex-1" />
          <Skeleton variant="text" height="3.5rem" width="8rem" />
        </div>
        
        {/* Filter pills skeleton */}
        <div className="flex flex-wrap justify-center gap-2 mb-8">
          {Array.from({ length: 5 }).map((_, i) => (
            <Skeleton key={i} variant="text" height="2.5rem" width="5rem" />
          ))}
        </div>
        
        {/* Profession cards skeleton */}
        <div className="grid grid-cols-2 md:grid-cols-3 gap-4 max-w-2xl mx-auto w-full">
          {Array.from({ length: 6 }).map((_, i) => (
            <Skeleton key={i} variant="card" height="8rem" />
          ))}
        </div>
      </div>
    </div>
  );
}

/**
 * Skeleton for auth pages (login, register, forgot password)
 */
export function AuthSkeleton() {
  return (
    <div className="min-h-[calc(100vh-4rem)] flex items-center justify-center px-4 py-12">
      <div className="w-full max-w-md">
        <div className="bg-bg-surface border border-border rounded-xl p-8 shadow-lg">
          {/* Header skeleton */}
          <div className="text-center mb-8">
            <Skeleton variant="text" height="2rem" width="60%" className="mx-auto mb-2" />
            <Skeleton variant="text" height="1rem" width="80%" className="mx-auto" />
          </div>
          
          {/* Form fields skeleton */}
          <div className="space-y-5">
            <Skeleton variant="text" height="2.5rem" />
            <Skeleton variant="text" height="2.5rem" />
            <Skeleton variant="text" height="2.5rem" />
          </div>
          
          {/* Button skeleton */}
          <div className="mt-6">
            <Skeleton variant="text" height="3rem" />
          </div>
          
          {/* Separator skeleton */}
          <div className="flex items-center my-6">
            <Skeleton variant="text" height="0.5rem" className="flex-1" />
            <Skeleton variant="text" height="1rem" width="4rem" className="mx-4" />
            <Skeleton variant="text" height="0.5rem" className="flex-1" />
          </div>
          
          {/* OAuth button skeleton */}
          <Skeleton variant="text" height="2.5rem" />
          
          {/* Link skeleton */}
          <div className="mt-6 text-center">
            <Skeleton variant="text" height="1rem" width="70%" className="mx-auto" />
          </div>
        </div>
      </div>
    </div>
  );
}

/**
 * Skeleton for search results page
 */
export function SearchResultsSkeleton() {
  return (
    <div className="max-w-5xl mx-auto px-4 py-8">
      {/* Search header skeleton */}
      <div className="mb-6">
        <Skeleton variant="text" height="2rem" width="50%" className="mb-2" />
        <Skeleton variant="text" height="1rem" width="30%" />
      </div>
      
      {/* Results skeleton */}
      <div className="space-y-4">
        {Array.from({ length: 5 }).map((_, i) => (
          <div key={i} className="bg-bg-surface border border-border rounded-lg p-4">
            <div className="flex items-start justify-between mb-2">
              <Skeleton variant="text" height="1.5rem" width="60%" />
              <Skeleton variant="text" height="1.5rem" width="5rem" />
            </div>
            <Skeleton variant="text" lines={2} className="mb-2" />
            <div className="flex gap-2">
              <Skeleton variant="text" height="1rem" width="4rem" />
              <Skeleton variant="text" height="1rem" width="4rem" />
              <Skeleton variant="text" height="1rem" width="6rem" />
            </div>
          </div>
        ))}
      </div>
      
      {/* Pagination skeleton */}
      <div className="flex justify-center gap-2 mt-6">
        {Array.from({ length: 5 }).map((_, i) => (
          <Skeleton key={i} variant="text" height="2.5rem" width="2.5rem" />
        ))}
      </div>
    </div>
  );
}

/**
 * Skeleton for profile page
 */
export function ProfileSkeleton() {
  return (
    <div className="max-w-2xl mx-auto px-4 py-8">
      {/* Profile header skeleton */}
      <div className="bg-bg-surface border border-border rounded-lg p-6 mb-6">
        <div className="flex items-center gap-4 mb-4">
          <Skeleton variant="circle" width="4rem" height="4rem" />
          <div className="flex-1">
            <Skeleton variant="text" height="1.5rem" width="50%" className="mb-2" />
            <Skeleton variant="text" height="1rem" width="40%" />
          </div>
        </div>
        <Skeleton variant="text" height="2.5rem" width="8rem" />
      </div>
      
      {/* Profile form skeleton */}
      <div className="bg-bg-surface border border-border rounded-lg p-6 space-y-4">
        <div>
          <Skeleton variant="text" height="1rem" width="6rem" className="mb-2" />
          <Skeleton variant="text" height="2.5rem" />
        </div>
        <div>
          <Skeleton variant="text" height="1rem" width="6rem" className="mb-2" />
          <Skeleton variant="text" height="2.5rem" />
        </div>
        <div>
          <Skeleton variant="text" height="1rem" width="6rem" className="mb-2" />
          <Skeleton variant="text" height="2.5rem" />
        </div>
        <Skeleton variant="text" height="3rem" width="8rem" className="mt-4" />
      </div>
    </div>
  );
}

/**
 * Skeleton for legal pages (privacy, terms, cookies)
 */
export function LegalPageSkeleton() {
  return (
    <div className="max-w-4xl mx-auto px-4 py-12">
      <Skeleton variant="text" height="2.5rem" width="60%" className="mb-4" />
      <Skeleton variant="text" height="1rem" width="40%" className="mb-8" />
      <div className="space-y-4">
        {Array.from({ length: 8 }).map((_, i) => (
          <div key={i}>
            <Skeleton variant="text" height="1.5rem" width={`${80 + Math.random() * 20}%`} className="mb-2" />
            <Skeleton variant="text" lines={3} />
          </div>
        ))}
      </div>
    </div>
  );
}

/**
 * Skeleton for pricing page
 */
export function PricingSkeleton() {
  return (
    <div className="max-w-5xl mx-auto px-4 py-12">
      <Skeleton variant="text" height="2.5rem" width="40%" className="mx-auto mb-2" />
      <Skeleton variant="text" height="1rem" width="60%" className="mx-auto mb-8" />
      <div className="grid md:grid-cols-3 gap-6">
        {Array.from({ length: 3 }).map((_, i) => (
          <div key={i} className="bg-bg-surface border border-border rounded-lg p-6">
            <Skeleton variant="text" height="1.5rem" width="50%" className="mb-2" />
            <Skeleton variant="text" height="3rem" width="60%" className="mb-4" />
            <div className="space-y-2 mb-4">
              {Array.from({ length: 4 }).map((_, j) => (
                <Skeleton key={j} variant="text" height="1rem" />
              ))}
            </div>
            <Skeleton variant="text" height="2.5rem" />
          </div>
        ))}
      </div>
    </div>
  );
}

/**
 * Skeleton for changelog page (US-028)
 */
export function ChangelogSkeleton() {
  return (
    <div className="max-w-4xl mx-auto px-4 py-8">
      {/* Hero skeleton */}
      <div className="mb-8">
        <Skeleton variant="text" height="2.5rem" width="50%" className="mb-3" />
        <Skeleton variant="text" height="1.25rem" width="60%" className="mb-2" />
        <Skeleton variant="text" height="1rem" width="40%" />
      </div>

      {/* Today's digest hero skeleton */}
      <div className="bg-bg-surface border border-border rounded-xl p-6 mb-8">
        <div className="flex items-center gap-3 mb-4">
          <Skeleton variant="circle" width="2.5rem" height="2.5rem" />
          <div>
            <Skeleton variant="text" height="1.25rem" width="12rem" className="mb-1" />
            <Skeleton variant="text" height="1rem" width="8rem" />
          </div>
        </div>
        <div className="space-y-4">
          {Array.from({ length: 3 }).map((_, i) => (
            <div key={i} className="py-3 border-b border-border last:border-b-0">
              <Skeleton variant="text" height="1rem" width="3rem" className="mb-2" />
              <Skeleton variant="text" height="1.25rem" width="80%" className="mb-1" />
              <Skeleton variant="text" height="0.875rem" width="40%" />
            </div>
          ))}
        </div>
      </div>

      {/* Filter pills skeleton */}
      <div className="flex gap-2 mb-6">
        {Array.from({ length: 5 }).map((_, i) => (
          <Skeleton key={i} variant="text" height="2rem" width="4rem" />
        ))}
      </div>

      {/* Stats skeleton */}
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-4 mb-8">
        {Array.from({ length: 4 }).map((_, i) => (
          <div key={i} className="bg-bg-surface border border-border rounded-lg p-4 text-center">
            <Skeleton variant="text" height="1.5rem" width="3rem" className="mx-auto mb-1" />
            <Skeleton variant="text" height="0.875rem" width="4rem" className="mx-auto" />
          </div>
        ))}
      </div>

      {/* Cards skeleton */}
      <div className="space-y-4">
        {Array.from({ length: 3 }).map((_, i) => (
          <div key={i} className="bg-bg-surface border border-border rounded-lg p-4">
            <div className="flex items-center justify-between mb-4">
              <Skeleton variant="text" height="1.25rem" width="40%" />
              <Skeleton variant="text" height="1.5rem" width="5rem" />
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

/**
 * Skeleton for dashboard page (US-032)
 */
export function DashboardSkeleton() {
  return (
    <div className="min-h-[calc(100vh-4rem)] flex">
      {/* Sidebar skeleton - hidden on mobile */}
      <aside className="hidden md:block md:w-64 lg:w-72 bg-bg-surface border-r border-border">
        <div className="p-4 space-y-2">
          {Array.from({ length: 5 }).map((_, i) => (
            <Skeleton key={i} variant="text" height="2.5rem" />
          ))}
        </div>
      </aside>

      {/* Main content */}
      <main className="flex-1 p-4 md:p-6 lg:p-8">
        {/* Welcome header skeleton */}
        <div className="mb-8">
          <Skeleton variant="text" height="2rem" width="40%" className="mb-2" />
          <Skeleton variant="text" height="1.25rem" width="20%" />
        </div>

        {/* Content grid */}
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          {/* Main content - 2 columns */}
          <div className="lg:col-span-2 space-y-6">
            {/* Relevant legislation skeleton */}
            <div className="bg-bg-surface border border-border rounded-xl p-6">
              <div className="flex justify-between mb-4">
                <Skeleton variant="text" height="1.5rem" width="40%" />
                <Skeleton variant="text" height="1rem" width="5rem" />
              </div>
              <div className="space-y-3">
                {Array.from({ length: 3 }).map((_, i) => (
                  <div key={i} className="p-3 border border-border rounded-lg">
                    <div className="flex items-center gap-2 mb-2">
                      <Skeleton variant="text" height="1rem" width="3rem" />
                      <Skeleton variant="text" height="1rem" width="6rem" />
                    </div>
                    <Skeleton variant="text" height="1rem" className="mb-1" />
                    <Skeleton variant="text" height="0.75rem" width="60%" />
                  </div>
                ))}
              </div>
            </div>

            {/* Recent changes skeleton */}
            <div className="bg-bg-surface border border-border rounded-xl p-6">
              <div className="flex justify-between mb-4">
                <Skeleton variant="text" height="1.5rem" width="40%" />
                <Skeleton variant="text" height="1rem" width="5rem" />
              </div>
              <div className="space-y-3">
                {Array.from({ length: 3 }).map((_, i) => (
                  <div key={i} className="p-3 border border-border rounded-lg">
                    <div className="flex items-center gap-2 mb-2">
                      <Skeleton variant="text" height="1rem" width="4rem" />
                      <Skeleton variant="text" height="1rem" width="3rem" />
                      <Skeleton variant="text" height="1rem" width="3rem" />
                    </div>
                    <Skeleton variant="text" height="1rem" className="mb-1" />
                    <div className="flex gap-1">
                      <Skeleton variant="text" height="0.75rem" width="2rem" />
                      <Skeleton variant="text" height="0.75rem" width="2rem" />
                      <Skeleton variant="text" height="0.75rem" width="2rem" />
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>

          {/* Sidebar - 1 column */}
          <div>
            {/* Quick actions skeleton */}
            <div className="bg-bg-surface border border-border rounded-xl p-6">
              <Skeleton variant="text" height="1.5rem" width="40%" className="mb-4" />
              <div className="flex gap-2 mb-6">
                <Skeleton variant="text" height="2.5rem" className="flex-1" />
                <Skeleton variant="text" height="2.5rem" width="2.5rem" />
              </div>
              <div className="grid grid-cols-2 gap-3">
                {Array.from({ length: 4 }).map((_, i) => (
                  <div key={i} className="p-4 border border-border rounded-lg">
                    <Skeleton variant="circle" width="2rem" height="2rem" className="mb-2" />
                    <Skeleton variant="text" height="0.875rem" className="mb-1" />
                    <Skeleton variant="text" height="0.75rem" width="60%" />
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      </main>
    </div>
  );
}
